import { QuestionItem, ValidationFormat, ValidationRules } from "../types/form";
import { validateUsSsn } from "./usSsnValidation";
import { validateUsPhone } from "./usPhoneValidation";
import { validateUsANumber } from "./usANumberValidation";
import { validateUscisAccount } from "./uscisAccountValidation";
import { validateEmail } from "./emailValidation";
import { validateUsZip } from "./usZipValidation";
import {
  formatLocalIsoDate,
  hasDateBounds,
  validateDateRange,
} from "./dateRangeValidation";
import { getValidationRules } from "./validationRules";

export type CompositeFieldDef = {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  order?: number;
  options?: Array<{ label?: string; value?: string; text?: string }>;
  format?: ValidationFormat | string;
  minDate?: string;
  maxDate?: string;
  min?: number;
  max?: number;
};

function isSubFieldEmpty(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === "";
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const N400_EMPLOYMENT_QUESTION_ID = 65;
const EMPLOYMENT_END_FIELD_KEY = "EMP_TO";
const PRESENT_VALUE = "PRESENT";

type CompositeEntry = Record<string, unknown>;

function hasAnyEntryValue(entry: CompositeEntry): boolean {
  return Object.values(entry).some((value) => !isSubFieldEmpty(value));
}

function toEntries(value: unknown): CompositeEntry[] {
  const entries = Array.isArray(value) ? value : [value];
  return entries.filter(
    (entry): entry is CompositeEntry =>
      !!entry && typeof entry === "object" && !Array.isArray(entry),
  );
}

function isFirstN400EmploymentPresent(
  question: QuestionItem,
  entryIndex: number,
  fieldKey: string,
  value: unknown,
): boolean {
  return (
    Number(question.id ?? question.originalQuestion?.id) ===
      N400_EMPLOYMENT_QUESTION_ID &&
    entryIndex === 0 &&
    fieldKey === EMPLOYMENT_END_FIELD_KEY &&
    String(value ?? "").trim().toUpperCase() === PRESENT_VALUE
  );
}

function normalizeN400EmploymentPresentForTimeline(
  question: QuestionItem,
  entries: CompositeEntry[],
  referenceDate = new Date(),
): CompositeEntry[] {
  if (Number(question.id ?? question.originalQuestion?.id) !== N400_EMPLOYMENT_QUESTION_ID) {
    return entries;
  }
  return entries.map((entry, entryIndex) =>
    isFirstN400EmploymentPresent(
      question,
      entryIndex,
      EMPLOYMENT_END_FIELD_KEY,
      entry[EMPLOYMENT_END_FIELD_KEY],
    )
      ? { ...entry, [EMPLOYMENT_END_FIELD_KEY]: formatLocalIsoDate(referenceDate) }
      : entry,
  );
}

function isoToUtcMs(value: string): number {
  const [year, month, day] = value.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function formatUtcIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(value: string, days: number): string {
  return formatUtcIsoDate(new Date(isoToUtcMs(value) + days * MS_PER_DAY));
}

function subtractYearsIso(referenceDate: Date, years: number): string {
  return formatUtcIsoDate(
    new Date(
      Date.UTC(
        referenceDate.getFullYear() - years,
        referenceDate.getMonth(),
        referenceDate.getDate(),
      ),
    ),
  );
}

function maxIso(a: string, b: string): string {
  return a > b ? a : b;
}

function minIso(a: string, b: string): string {
  return a < b ? a : b;
}

function formatDateForMessage(value: string): string {
  const [year, month, day] = value.split("-");
  return `${month}/${day}/${year}`;
}

function inferZipFormat(field: CompositeFieldDef): string | undefined {
  if (field.format) return String(field.format);
  if (/zip/i.test(String(field.key ?? ""))) return "us_zip";
  return undefined;
}

function validateFormatValue(format: string, value: unknown): string | null {
  if (format === "us_ssn") {
    const result = validateUsSsn(value);
    return result.ok ? null : result.message;
  }
  if (format === "us_phone") {
    const result = validateUsPhone(value);
    return result.ok ? null : result.message;
  }
  if (format === "us_a_number") {
    const result = validateUsANumber(value);
    return result.ok ? null : result.message;
  }
  if (format === "uscis_account") {
    const result = validateUscisAccount(value);
    return result.ok ? null : result.message;
  }
  if (format === "email") {
    const result = validateEmail(value);
    return result.ok ? null : result.message;
  }
  if (format === "us_zip") {
    const result = validateUsZip(value);
    return result.ok ? null : result.message;
  }
  return null;
}

export function validateCompositeSubField(
  field: CompositeFieldDef,
  value: unknown,
): string | null {
  const label = field.label || field.key || "Field";
  const prefix = `${label}: `;

  if (isSubFieldEmpty(value)) {
    if (field.required) return `${prefix}This field is required.`;
    return null;
  }

  const type = String(field.type ?? "text").toLowerCase();

  if (type === "date" || field.minDate || field.maxDate) {
    const result = validateDateRange(value, {
      minDate: field.minDate,
      maxDate: field.maxDate,
    });
    if (!result.ok) return `${prefix}${result.message}`;
  }

  const format = inferZipFormat(field);
  if (format) {
    const message = validateFormatValue(format, value);
    if (message) return `${prefix}${message}`;
  }

  if (type === "number") {
    const num = Number(value);
    if (Number.isNaN(num)) return `${prefix}Enter a valid number.`;
    if (field.min != null && num < Number(field.min)) {
      return `${prefix}Value is too low.`;
    }
    if (field.max != null && num > Number(field.max)) {
      return `${prefix}Value is too high.`;
    }
  }

  if (type === "select" || type === "dropdown" || type === "radio") {
    const options = Array.isArray(field.options) ? field.options : [];
    if (options.length > 0) {
      const allowed = options.map((opt) =>
        String(opt.value !== undefined ? opt.value : opt.label ?? opt.text ?? opt),
      );
      if (!allowed.includes(String(value))) {
        return `${prefix}Select a valid option.`;
      }
    }
  }

  return null;
}

export function getCompositeFields(question: QuestionItem): CompositeFieldDef[] {
  const rules = getValidationRules(question);
  const fields = rules?.fields;
  if (!Array.isArray(fields)) return [];
  return fields as CompositeFieldDef[];
}

export function compositeFieldsHaveConstraints(
  fields: CompositeFieldDef[],
): boolean {
  return fields.some(
    (field) =>
      field.required ||
      field.format ||
      field.minDate ||
      field.maxDate ||
      field.min != null ||
      field.max != null ||
      /zip/i.test(String(field.key ?? "")),
  );
}

function getAnswerByQuestionId(
  answers: Record<string, unknown> | undefined,
  questionId: number,
): unknown {
  if (!answers) return undefined;
  return (
    answers[`q_${questionId}`] ??
    answers[questionId] ??
    answers[String(questionId)]
  );
}

export function hasExactEntryCountConstraint(question: QuestionItem): boolean {
  const rules = getValidationRules(question);
  return Number(rules?.exactEntryCountFrom?.questionId) > 0;
}

function validateExactEntryCount(
  rules: ValidationRules | null,
  entries: CompositeEntry[],
  fields: CompositeFieldDef[],
  answers?: Record<string, unknown>,
): string | null {
  const config = rules?.exactEntryCountFrom;
  const sourceId = Number(config?.questionId);
  if (!Number.isInteger(sourceId) || sourceId <= 0) return null;

  const raw = getAnswerByQuestionId(answers, sourceId);
  if (raw === undefined || raw === null || raw === "") return null;
  const expected = Number(raw);
  if (!Number.isInteger(expected) || expected < 0) return null;

  const countsAsComplete =
    config?.countMode === "all_fields"
      ? (entry: CompositeEntry) =>
          fields.length > 0 &&
          fields.every((field) => !isSubFieldEmpty(entry[field.key]))
      : hasAnyEntryValue;
  const completed = entries.filter(countsAsComplete).length;
  if (completed === expected) return null;

  const noun = expected === 1 ? "record" : "records";
  return `Enter exactly ${expected} ${noun} to match the number you reported.`;
}

export function hasCompositeValidationConstraints(
  question: QuestionItem,
): boolean {
  if ((question.answerType ?? question.type) !== "composite") return false;
  const rules = getValidationRules(question);
  return Boolean(
    compositeFieldsHaveConstraints(getCompositeFields(question)) ||
      rules?.timelineCoverage?.enabled ||
      rules?.timelineEntries?.enabled ||
      hasExactEntryCountConstraint(question),
  );
}

type TimelineInterval = {
  from: string;
  to: string;
};

function validateIsoDateField(
  label: string,
  raw: unknown,
  fieldLabel: string,
): string | null {
  const value = String(raw ?? "").trim();
  if (!ISO_DATE.test(value)) {
    return `${label}: Enter a valid ${fieldLabel} date.`;
  }
  return null;
}

function validateTimelineCoverage(
  rules: ValidationRules,
  entries: CompositeEntry[],
  referenceDate = new Date(),
): string | null {
  const config = rules.timelineCoverage;
  if (!config?.enabled) return null;

  const label = config.coverageLabel || "Timeline";
  const fromField = config.fromField;
  const toField = config.toField;
  const windowYears = Number(config.windowYears || 5);
  const maxGapDays = Number(config.maxGapDays ?? 0);
  const windowStart = subtractYearsIso(referenceDate, windowYears);
  const windowEnd = formatLocalIsoDate(referenceDate);
  const intervals: TimelineInterval[] = [];

  for (const entry of entries.filter(hasAnyEntryValue)) {
    const rawFrom = entry[fromField];
    const rawTo = entry[toField];
    const from = String(rawFrom ?? "").trim();
    let to = String(rawTo ?? "").trim();

    if (!from) {
      return `${label}: Enter a start date for every entry.`;
    }
    const fromMessage = validateIsoDateField(label, from, "start");
    if (fromMessage) return fromMessage;

    if (!to && config.allowOpenEndedCurrent) {
      to = windowEnd;
    }
    if (!to) {
      return `${label}: Enter an end date for every entry, or leave only the current entry open.`;
    }
    const toMessage = validateIsoDateField(label, to, "end");
    if (toMessage) return toMessage;

    if (from > to) {
      return `${label}: Start date cannot be after end date.`;
    }

    const clippedFrom = maxIso(from, windowStart);
    const clippedTo = minIso(to, windowEnd);
    if (clippedFrom <= clippedTo) {
      intervals.push({ from: clippedFrom, to: clippedTo });
    }
  }

  if (intervals.length === 0) {
    return `${label}: Add entries covering every day from ${formatDateForMessage(
      windowStart,
    )} to ${formatDateForMessage(windowEnd)}.`;
  }

  intervals.sort((a, b) => a.from.localeCompare(b.from));

  let coveredThrough: string | null = null;
  for (const interval of intervals) {
    if (!coveredThrough) {
      if (interval.from > windowStart) {
        return `${label}: Missing coverage from ${formatDateForMessage(
          windowStart,
        )} to ${formatDateForMessage(addDays(interval.from, -1))}.`;
      }
      coveredThrough = interval.to;
      continue;
    }

    const gapStart = addDays(coveredThrough, 1);
    const gapDays =
      (isoToUtcMs(interval.from) - isoToUtcMs(gapStart)) / MS_PER_DAY;
    if (gapDays > maxGapDays) {
      return `${label}: Missing coverage from ${formatDateForMessage(
        gapStart,
      )} to ${formatDateForMessage(addDays(interval.from, -1))}.`;
    }

    if (interval.to > coveredThrough) {
      coveredThrough = interval.to;
    }
  }

  if (!coveredThrough || coveredThrough < windowEnd) {
    return `${label}: Missing coverage from ${formatDateForMessage(
      addDays(coveredThrough || windowStart, coveredThrough ? 1 : 0),
    )} to ${formatDateForMessage(windowEnd)}.`;
  }

  return null;
}

function validateTimelineEntries(
  rules: ValidationRules,
  entries: CompositeEntry[],
  referenceDate = new Date(),
): string | null {
  const config = rules.timelineEntries;
  if (!config?.enabled) return null;

  const label = config.coverageLabel || "Timeline";
  const fromField = config.fromField;
  const toField = config.toField;
  const windowYears = Number(config.windowYears || 5);
  const windowStart = subtractYearsIso(referenceDate, windowYears);
  const windowEnd = formatLocalIsoDate(referenceDate);
  const intervals: TimelineInterval[] = [];

  for (const entry of entries.filter(hasAnyEntryValue)) {
    const from = String(entry[fromField] ?? "").trim();
    const to = String(entry[toField] ?? "").trim();

    if (!from && !to) continue;
    if (!from || !to) {
      return `${label}: Enter both start and end dates for each entry.`;
    }
    const fromMessage = validateIsoDateField(label, from, "start");
    if (fromMessage) return fromMessage;
    const toMessage = validateIsoDateField(label, to, "end");
    if (toMessage) return toMessage;
    if (from > to) {
      return `${label}: Start date cannot be after end date.`;
    }
    if (config.requireWithinWindow && (from < windowStart || to > windowEnd)) {
      return `${label}: Dates must be within the last ${windowYears} years.`;
    }
    intervals.push({ from, to });
  }

  if (config.preventOverlaps && intervals.length > 1) {
    intervals.sort((a, b) => a.from.localeCompare(b.from));
    let previous = intervals[0];
    for (const interval of intervals.slice(1)) {
      if (interval.from <= previous.to) {
        return `${label}: Entries cannot overlap.`;
      }
      previous = interval.to > previous.to ? interval : previous;
    }
  }

  return null;
}

export function validateCompositeAnswer(
  question: QuestionItem,
  value: unknown,
  answers?: Record<string, unknown>,
): string | null {
  const fields = getCompositeFields(question);
  const rules = getValidationRules(question);
  if (fields.length === 0 && !rules) return null;

  const sorted = [...fields].sort((a, b) => {
    const orderA = a.order != null ? a.order : 999;
    const orderB = b.order != null ? b.order : 999;
    return orderA - orderB;
  });

  const entries = toEntries(value);

  for (let entryIndex = 0; entryIndex < entries.length; entryIndex += 1) {
    const entry = entries[entryIndex];
    if (!hasAnyEntryValue(entry)) continue;
    for (const field of sorted) {
      if (isFirstN400EmploymentPresent(question, entryIndex, field.key, entry[field.key])) {
        continue;
      }
      const message = validateCompositeSubField(field, entry[field.key]);
      if (message) return message;
    }
  }

  if (rules) {
    const timelineEntries = normalizeN400EmploymentPresentForTimeline(question, entries);
    const coverageMessage = validateTimelineCoverage(rules, timelineEntries);
    if (coverageMessage) return coverageMessage;

    const entriesMessage = validateTimelineEntries(rules, entries);
    if (entriesMessage) return entriesMessage;

    const exactCountMessage = validateExactEntryCount(
      rules,
      entries,
      fields,
      answers,
    );
    if (exactCountMessage) return exactCountMessage;
  }

  return null;
}
